import React from "react";
import { useExemptionsContext } from "../../ExemptionsContext";
import ExemptionsEstimateCard from "../ExemptionsEstimateCard";
import NeverOverpayValuePropIconSavingsOrFree from "public/never-overpay-value-prop2.svg";
import Filing from "public/account/exemptions/filing.svg";
import { useCallback, useEffect, useRef, useState } from "react";
import { usePropertyContext } from "../../context";
import client from "utils/client";
import moment from "moment";
import { sendErrorToWebhook } from "utils/sendWebhook";
import lottie from "lottie-web/build/player/lottie_light";
import ExemptionsLoadingAnimation from "public/account/exemptions/Exemption-Loading.json";
import { useEventTracker } from "utils/useEventTracker";
import constants from "utils/constants";
import ButtonV2 from "elements/ButtonV2";

const NEW_HOMESTEAD_TITLE = "Good news! You qualify for a homestead exemption.";
const RETRO_HOMESTEAD_TITLE =
	"Great news! We think you could receive a refund from past unused exemptions!";

const STAR_CREDIT_TITLE = "Good news! You qualify for a STAR Credit.";
const RETRO_STAR_CREDIT_TITLE =
	"Good news! We think you could receive a refund from past unused STAR Credits!";

const NEW_HOMESTEAD_BODY =
	"This will lower your home’s taxable value for years to come. The best part? It’s free to file with us! Just answer a few more questions and leave the rest to us.";
const RETRO_HOMESTEAD_BODY =
	"You're also eligible for the Homestead exemption, which we will help you set up at no extra charge.";

const STAR_CREDIT_BODY =
	"This will lower your home’s taxable value for years to come. The best part? It’s free to file with us! Just answer a few more questions and leave the rest to us.";
const RETRO_STAR_CREDIT_BODY =
	"You're also eligible for the STAR Credit going forward, which we will help you set up at no extra charge.";

const NOT_SURE_EXISTING_HOMESTEAD_DISCLAIMER =
	"Please Keep In Mind: This estimate is determined assuming you don't have an exemption already since you indicated that you were not sure. If we determine you already have an active exemption then there may be little to no refund available.";

const NEW_HOMESTEAD_SECTION = (flowId: string) => {
	if (flowId === "NEW_YORK_EXEMPTION") {
		return (
			<>
				<p className="sm italic bold text-center mt-2">
					It’s free to file with Ownwell!
				</p>

				<div className="exemptions-question-card mt-4">
					<p className="bold">
						What’s a <span className="no-translate">STAR Credit</span>?
					</p>
					<p className="sm">
						<span className="no-translate">STAR Credit</span> is a program in
						New York that provides eligible homeowners with a reduction on their
						school property taxes through a yearly credit.
					</p>
				</div>
			</>
		);
	}

	if (flowId === "ILLINOIS_EXEMPTION") {
		return (
			<>
				<p className="sm italic bold text-center mt-2">
					It’s free to file with Ownwell!
				</p>

				<div className="exemptions-question-card mt-4">
					<p className="bold">What’s a homestead exemption?</p>
					<p className="sm">
						By lowering the taxable value of your home, a homestead exemption
						directly translates into lower property taxes. In Illinois, a
						homestead exemption also provides a safeguard against excessive
						property tax increases. The exemption reduces the Equalized Assessed
						Value (EAV) of a property by $10,000, preventing sudden spikes in
						your tax bill.
					</p>
				</div>
			</>
		);
	}

	const state = () => {
		if (flowId === "TEXAS_EXEMPTION") {
			return "Texas";
		} else if (flowId === "GEORGIA_EXEMPTION") {
			return "Georgia";
		} else {
			return "California";
		}
	};

	return (
		<>
			<p className="sm italic bold text-center mt-2">
				It’s free to file with Ownwell!
			</p>

			<div className="exemptions-question-card mt-4">
				<p className="bold">What’s a homestead exemption?</p>
				<p className="sm">
					By lowering the taxable value of your home, a homestead exemption
					directly translates into lower property taxes. In {state()}, a
					homestead exemption also provides a safeguard against excessive
					property tax increases. The taxable value of your homestead can only
					increase by a maximum of 10% per year, preventing sudden spikes in
					your tax bill.
				</p>
			</div>
		</>
	);
};

const RETRO_HOMESTEAD_SECTION = (flowId: string) => {
	const { details } = usePropertyContext();

	if (flowId === "NEW_YORK_EXEMPTION") {
		return (
			<>
				<div className="exemptions-question-card mt-4">
					<p className="bold">How much does this cost?</p>
					<p className="sm">
						With our contingency fee structure, we only charge 35% of the final
						retroactive refund amount. This means if you don't see any actual
						savings, we won't charge you anything.
					</p>
				</div>
				<div className="exemptions-question-card mt-4">
					<div className="exemptions-homestead-incentives-cards-container">
						<div className="exemptions-homestead-incentives-card">
							<NeverOverpayValuePropIconSavingsOrFree />
							<p className="bold">Savings-or-Free Guarantee</p>
							<p className="sm">
								We'll handle your application at no cost if it doesn't result in
								savings.
							</p>
						</div>
						<div className="exemptions-homestead-incentives-card">
							<Filing />
							<p className="bold">Filing and Tracking</p>
							<p className="sm">
								We will work with the county directly on your behalf to
								pro-actively keep you up to date.
							</p>
						</div>
					</div>
				</div>
				<div className="exemptions-question-card mt-4">
					<p className="bold">
						What’s a <span className="no-translate">STAR Credit</span>?
					</p>
					<p className="sm">
						<span className="no-translate">STAR Credit</span> is a program in
						New York that provides eligible homeowners with a reduction on their
						school property taxes through a yearly credit.
					</p>
				</div>
			</>
		);
	}

	return (
		<>
			<div className="exemptions-question-card mt-4">
				<p className="bold">How much does this cost?</p>
				<p className="sm">
					With our contingency fee structure, we only charge{" "}
					{details?.contingency}% of the final retroactive refund amount. This
					means if you don't see any actual savings, we won't charge you
					anything.
				</p>
			</div>
			<div className="exemptions-question-card mt-4">
				<div className="exemptions-homestead-incentives-cards-container">
					<div className="exemptions-homestead-incentives-card">
						<NeverOverpayValuePropIconSavingsOrFree />
						<p className="bold">Savings-or-Free Guarantee</p>
						<p className="sm">
							We'll handle your application at no cost if it doesn't result in
							savings.
						</p>
					</div>
					<div className="exemptions-homestead-incentives-card">
						<Filing />
						<p className="bold">Filing and Tracking</p>
						<p className="sm">
							We will work with the county directly on your behalf to
							pro-actively keep you up to date.
						</p>
					</div>
				</div>
			</div>
			{flowId === "ILLINOIS_EXEMPTION" && (
				<div className="exemptions-question-card mt-4">
					<p className="bold">What’s a homestead exemption?</p>
					<p className="sm">
						By lowering the taxable value of your home, a homestead exemption
						directly translates into lower property taxes. In Illinois, a
						homestead exemption also provides a safeguard against excessive
						property tax increases. The exemption reduces the Equalized Assessed
						Value (EAV) of a property by $10,000, preventing sudden spikes in
						your tax bill.
					</p>
				</div>
			)}
		</>
	);
};

const HomesteadQualified = () => {
	const [loading, setLoading] = useState(true);
	const {
		flowId,
		next,
		isEligibleForRetro,
		currentlyReceivingValue,
		moveInDateValue,
		purchaseDateValue,
		futureSavingsAmount,
		setFutureSavingsAmount,
		retroSavingsAmount,
		setRetroSavingsAmount,
	} = useExemptionsContext();

	const { details } = usePropertyContext();
	const trackEvent = useEventTracker();

	const fetchExemptionSavings = useCallback(async () => {
		try {
			if (!details) return;
			const { data } = await client.getExemptionsSavingsData({
				propertyId: details?.id,
				moveInDate: moment(moveInDateValue).format("YYYY-MM-DD"),
				purchaseDate: moment(purchaseDateValue).format("YYYY-MM-DD"),
			});
			setFutureSavingsAmount(data?.futureSavings);
			setRetroSavingsAmount(data?.retroSavings);
		} catch (e: any) {
			console.error("Error fetching retro exemption savings: ", e);
			sendErrorToWebhook("Error fetching retro exemption savings: ", e);
		} finally {
			setTimeout(() => {
				setLoading(false);
			}, 4000);
		}
	}, [details, moveInDateValue]);

	useEffect(() => {
		fetchExemptionSavings();
	}, [fetchExemptionSavings]);

	const animationRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!animationRef.current) return;

		const savingsAnimation = lottie.loadAnimation({
			container: animationRef.current,
			renderer: "svg",
			name: "savings",
			loop: true,
			autoplay: true,
			animationData: ExemptionsLoadingAnimation,
			rendererSettings: {
				className: "exemption-savings-animtion",
			},
		});

		savingsAnimation.setSpeed(0.85);

		return () => {
			savingsAnimation.destroy();
		};
	}, []);

	const handleSubmit = async () => {
		if (isEligibleForRetro) {
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Estimates_Refund_Next_Button,
				data: {
					Amount: retroSavingsAmount
						? "$" + retroSavingsAmount
						: "No retro savings amount",
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
		} else {
			trackEvent({
				eventName:
					constants.EVENTS.Exemptions_Flow_Estimates_No_Refund_Next_Button,
				data: {
					Amount: "$" + futureSavingsAmount,
					"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
						? "Yes"
						: "No",
				},
			});
		}
		const trackingEventData = {
			isEligibleForRetro,
			Amount: isEligibleForRetro
				? retroSavingsAmount
					? "$" + retroSavingsAmount
					: "No retro savings amount"
				: "$" + futureSavingsAmount,
		};
		next({ trackingEventData });
	};

	useEffect(() => {
		if (!loading) {
			if (isEligibleForRetro) {
				trackEvent({
					eventName: constants.EVENTS.Exemptions_Flow_Estimates_Refund_View,
					data: {
						Amount: retroSavingsAmount
							? retroSavingsAmount
							: constants.EXEMPTIONS_AVERAGE_SAVINGS,
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
						Address: details?.address,
					},
				});
			} else {
				trackEvent({
					eventName: constants.EVENTS.Exemptions_Flow_Estimates_No_Refund_View,
					data: {
						Amount: constants.EXEMPTIONS_AVERAGE_SAVINGS,
						"Is End-To-End Signup": details?.exemption?.is_end_to_end_signup
							? "Yes"
							: "No",
						Address: details?.address,
					},
				});
			}
		}
	}, [retroSavingsAmount, loading, isEligibleForRetro]);

	const showSavingsDisclaimer =
		currentlyReceivingValue && currentlyReceivingValue === "notSure";

	const title = React.useMemo(() => {
		if (isEligibleForRetro) {
			if (flowId === "NEW_YORK_EXEMPTION") {
				return RETRO_STAR_CREDIT_TITLE;
			} else {
				return RETRO_HOMESTEAD_TITLE;
			}
		} else {
			if (flowId === "NEW_YORK_EXEMPTION") {
				return STAR_CREDIT_TITLE;
			} else {
				return NEW_HOMESTEAD_TITLE;
			}
		}
	}, [isEligibleForRetro, flowId]);

	const body = React.useMemo(() => {
		if (isEligibleForRetro) {
			if (flowId === "NEW_YORK_EXEMPTION") {
				return RETRO_STAR_CREDIT_BODY;
			} else {
				return RETRO_HOMESTEAD_BODY;
			}
		} else {
			if (flowId === "NEW_YORK_EXEMPTION") {
				return STAR_CREDIT_BODY;
			} else {
				return NEW_HOMESTEAD_BODY;
			}
		}
	}, [isEligibleForRetro, flowId]);

	const additionalInfo = React.useMemo(() => {
		if (!flowId) return null;
		return isEligibleForRetro
			? RETRO_HOMESTEAD_SECTION(flowId)
			: NEW_HOMESTEAD_SECTION(flowId);
	}, [isEligibleForRetro, flowId]);

	if (loading) {
		return (
			<div id="exemption-survey-savings-estimate-loading-animation">
				<div ref={animationRef} />
			</div>
		);
	}

	return (
		<div>
			<p className="lg bold">{title}</p>
			<p className="exemptions-survey-retro-body">{body}</p>
			<ExemptionsEstimateCard retro={isEligibleForRetro} />
			{showSavingsDisclaimer && (
				<p className="body-tiny mt-2">
					<b>{NOT_SURE_EXISTING_HOMESTEAD_DISCLAIMER}</b>
				</p>
			)}
			{!showSavingsDisclaimer && flowId === "NEW_YORK_EXEMPTION" && (
				<p className="body-tiny mt-2 denim_5">
					*Actual tax savings may vary. This is only an estimate and assumes
					this property is your primary residence and you do not have an active
					primary residence exemption or credit elsewhere.
				</p>
			)}
			<ButtonV2
				size="large"
				id="account-property-profile-intro-begin"
				onClick={handleSubmit}>
				Continue
			</ButtonV2>
			{additionalInfo}
		</div>
	);
};

export default HomesteadQualified;
